import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './BidCard.css';

const API = process.env.REACT_APP_API_URL;

function fmt(val) {
  return parseFloat(val || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function BidCard({ bid, isProducer, onRespond }) {
  const { t } = useTranslation();
  const b = bid;
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);

  const priceDiff = b.market_price_ref
    ? ((parseFloat(b.price_per_box) - b.market_price_ref) / b.market_price_ref * 100).toFixed(1)
    : null;

  useEffect(() => {
    if (chatOpen) {
      axios.get(`${API}/market/bids/${b.id}/messages/`)
        .then(res => setMessages(res.data))
        .catch(() => {});
    }
  }, [chatOpen, b.id]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/market/bids/${b.id}/messages/`, { text: newMsg.trim() });
      setMessages(prev => [...prev, res.data]);
      setNewMsg('');
    } catch { /* */ }
    finally { setSending(false); }
  };

  return (
    <div className={`bid-card bmp-card bid-card--${b.status}`}>
      <div className="bid-card-header">
        <div className="bid-card-who">
          {b.bidder_photo && <img src={b.bidder_photo} alt="" className="bid-card-avatar" />}
          <div>
            <strong>{b.bidder_company || b.bidder_username}</strong>
            <span className="bid-card-role">{b.bidder_role_display}</span>
          </div>
        </div>
        <span className={`bmp-status bmp-status--${b.status}`}>{b.status_display}</span>
      </div>

      <div className="bid-card-details">
        <span><strong>{b.boxes_requested.toLocaleString('es-EC')}</strong> {t('mkt_boxes_label')}</span>
        <span><strong>${fmt(b.price_per_box)}</strong>/{t('wc_per_box')}</span>
        <span className="bid-card-total">${fmt(b.total)}</span>
        <span className="bmp-tag">{b.payment_terms_display}</span>
        {b.brand && <span className="bmp-tag">{b.brand}</span>}
        {b.pickup_location && <span className="bmp-tag">{b.pickup_location}</span>}
        {b.load_type_display && <span className="bmp-tag">{b.load_type_display}</span>}
      </div>

      {/* Market price reference */}
      {b.market_price_ref && (
        <div className="bid-card-ref">
          {t('bmp_market_ref')}: <strong>${fmt(b.market_price_ref)}</strong>
          <span className={`bid-card-diff${parseFloat(priceDiff) >= 0 ? ' bid-card-diff--up' : ' bid-card-diff--down'}`}>
            {parseFloat(priceDiff) >= 0 ? '+' : ''}{priceDiff}%
          </span>
        </div>
      )}

      {b.notes && <p className="bid-card-notes">{b.notes}</p>}

      <div className="bid-card-bottom">
        {isProducer && b.status === 'pending' && onRespond && (
          <div className="bid-card-actions">
            <button className="bmp-btn bmp-btn-success" onClick={() => onRespond(b.id, 'accept')}>{t('mkt_accept')}</button>
            <button className="bmp-btn bmp-btn-danger" onClick={() => onRespond(b.id, 'reject')}>{t('mkt_reject')}</button>
          </div>
        )}
        <button className="bmp-btn-ghost bid-card-chat-toggle" onClick={() => setChatOpen(v => !v)}>
          &#128172; {t('bmp_chat')} {b.messages_count > 0 && `(${b.messages_count})`}
        </button>
      </div>

      {/* Chat */}
      {chatOpen && (
        <div className="bid-chat">
          <div className="bid-chat-messages">
            {messages.length === 0 && <p className="bid-chat-empty">{t('bmp_no_messages')}</p>}
            {messages.map(m => (
              <div key={m.id} className={`bid-chat-msg${m.sender_username === bid.bidder_username ? ' bid-chat-msg--bidder' : ' bid-chat-msg--producer'}`}>
                <span className="bid-chat-sender">@{m.sender_username}</span>
                <span className="bid-chat-text">{m.text}</span>
                <span className="bid-chat-time">{new Date(m.created_at).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
          <form className="bid-chat-input" onSubmit={sendMessage}>
            <input
              type="text"
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              placeholder={t('bmp_type_message')}
              maxLength={500}
            />
            <button type="submit" disabled={sending || !newMsg.trim()} className="bmp-btn bmp-btn-primary">
              {t('bmp_send')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
